import {DataStatus, Dialog} from 'platform/components';
import {Show} from 'platform/foundation';

import {useEffect, useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  GetTransferOrderItemResponse,
  useGetTransferOrderQuery,
  useLazyGetTransferOrderItemQuery,
  usePatchTransferOrderItemMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError, WAREHOUSE_GRID_CODES} from '@dms/shared';

import {ControlledRowControl, RowV5ResponseBody} from 'features/datagrid';

import {TransferOrderItemDetail} from './TransferOrderItemDetail';

export type TransferOrderItemEditProps = {
  transferOrderId: string;
  transferOrderItemId: string;
  dataQueryId?: string;
  onClose: VoidFunction;
};

export function TransferOrderItemEdit(props: TransferOrderItemEditProps) {
  const [currentItemId, setCurrentItemId] = useState(props.transferOrderItemId);

  const transferOrderQuery = useGetTransferOrderQuery(
    {transferOrderId: props.transferOrderId},
    {skip: isNilOrEmpty(props.transferOrderId)}
  );

  const [getTransferOrderItem, {isFetching: isLoadingItem}] = useLazyGetTransferOrderItemQuery();

  const [, patchStatus] = usePatchTransferOrderItemMutation({
    fixedCacheKey: 'edit-transfer-order-item',
  });

  const [item, setItem] = useState<GetTransferOrderItemResponse['transferOrderItem'] | null>(null);

  const isSavedByRowControlRef = useRef<boolean>(false);

  const formRef = useRef<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>(null);

  const isLoading = transferOrderQuery.isLoading || isLoadingItem;
  const isSavingChanges = patchStatus.isLoading;

  const handleGetItemDetail = async (itemId: string) => {
    await getTransferOrderItem({
      transferOrderId: props.transferOrderId,
      transferOrderItemId: itemId,
    })
      .unwrap()
      .then((result) => setItem(result.transferOrderItem))
      .catch(handleApiError);
  };

  useEffect(() => {
    if (isNil(currentItemId)) {
      return;
    }
    handleGetItemDetail(currentItemId);
    // Disable exhaustive-deps because handleGetItemDetail only depends on stable values
    // (transferOrderId prop and setItem state setter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItemId]);

  const isEditingDisabled = transferOrderQuery.data?.state === 'COMPLETED';

  const handlePageChange = async (row: RowV5ResponseBody) => {
    const isDirty = formRef.current?.isDirty;
    const submitForm = formRef.current?.submit;

    if (!isDirty) {
      setCurrentItemId(row.id);
      return true;
    }

    isSavedByRowControlRef.current = true;

    const isSubmitSuccessful = await submitForm?.();

    if (!isSubmitSuccessful || patchStatus.isError) {
      isSavedByRowControlRef.current = false;
      return false;
    }

    setCurrentItemId(row.id);
    return true;
  };

  return (
    <Dialog
      title={i18n.t('entity.warehouse.labels.editItem')}
      isOpen={isNotNil(currentItemId)}
      size="large"
      withAdditionalFooter
      onClose={props.onClose}
      pagination={
        <Show when={isNotNil(props.dataQueryId)}>
          <ControlledRowControl
            isDisabled={isSavingChanges}
            dataQueryId={props.dataQueryId as string}
            gridCode={WAREHOUSE_GRID_CODES.warehouseTransferOrderItems}
            entityId={currentItemId}
            onPageChange={handlePageChange}
          />
        </Show>
      }
    >
      <DataStatus isLoading={isLoading} minHeight={105}>
        {isNotNil(item) ? (
          <TransferOrderItemDetail
            key={currentItemId}
            isEditingDisabled={isEditingDisabled}
            transferOrderId={props.transferOrderId}
            transferOrderItemId={currentItemId}
            item={item}
            onClose={props.onClose}
            isSavedByRowControlRef={isSavedByRowControlRef}
            formRef={formRef}
          />
        ) : null}
      </DataStatus>
    </Dialog>
  );
}
